import { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { Map, OrderedMap } from 'immutable';

const useObserverTags = ({ uniqueKeysList }) => {
  const [elementsVisibilityState, setElementsVisibilityState] = useState(Map());
  const parentElRef = useRef(null);
  const elementsRef = useRef(OrderedMap());
  const observersRef = useRef(Map());

  const setElementRef = useCallback(
    (key) => (ref) => {
      elementsRef.current = elementsRef.current.set(key, ref);

      if (!ref) {
        elementsRef.current = elementsRef.current.delete(key);
      }
    },
    [],
  );

  const handleObserver = useCallback(
    (key) =>
      ([intersectionObject]) => {
        // when scrolling inside ag-grid we are getting false observer handler invocation
        // with root element (grid cell) having all dimensions from getBoundingClientRect equal to 0
        // though cell element is present in DOM and has proper sizing and positioning
        if (intersectionObject.rootBounds.width === 0 && intersectionObject.rootBounds.height === 0) return;

        setElementsVisibilityState((prevState) =>
          prevState.set(key, intersectionObject.intersectionRatio === 1 ? 1 : 0),
        );
      },
    [],
  );

  const initObservers = useCallback(() => {
    if (!parentElRef.current) return;

    const observerOptions = {
      root: parentElRef.current,
      rootMargin: '0px',
      threshold: 1,
    };

    elementsRef.current.entrySeq().forEach(([key, ref]) => {
      if (!ref) return;

      const existingObserver = observersRef.current.get(key);
      if (existingObserver) {
        existingObserver.disconnect();
      }

      const observer = new IntersectionObserver(handleObserver(key), observerOptions);
      observer.observe(ref);

      observersRef.current = observersRef.current.set(key, observer);
    });
  }, [handleObserver]);

  const { hiddenCount, lastVisibleKey, isAllHidden, lastKey } = useMemo(() => {
    const totalElements = elementsRef.current.size;

    if (totalElements === 0) {
      return {
        isAllHidden: false,
        hiddenCount: 0,
        lastVisibleKey: null,
        lastKey: null,
      };
    }

    // Count hidden elements (those explicitly marked as not visible)
    const hiddenElementsCount = elementsRef.current.keySeq().count((key) => elementsVisibilityState.get(key, 1) === 0);

    // Find the last visible key by iterating through elements in order
    let lastVisibleK = null;
    elementsRef.current
      .keySeq()
      .reverse()
      .forEach((key) => {
        if (lastVisibleK === null && elementsVisibilityState.get(key, 1) === 1) {
          lastVisibleK = key;
        }
      });

    const lastK = elementsRef.current.keySeq().last();
    const allHidden = hiddenElementsCount === totalElements;

    return {
      isAllHidden: allHidden,
      hiddenCount: hiddenElementsCount,
      lastVisibleKey: lastVisibleK,
      lastKey: lastK,
    };
  }, [elementsVisibilityState]);

  useEffect(() => {
    const currentKeys = observersRef.current.keySeq().toList();

    if (!uniqueKeysList.sort().equals(currentKeys.sort())) {
      setElementsVisibilityState(Map());

      // Filter elements to only include current keys
      elementsRef.current = elementsRef.current.filter((_, key) => uniqueKeysList.includes(key));

      // Clean up old observers
      observersRef.current.entrySeq().forEach(([key, observer]) => {
        observer.disconnect();
      });
      observersRef.current = Map();

      initObservers();
    }
  }, [uniqueKeysList, initObservers]);

  useEffect(
    () => () => {
      if (observersRef.current.size > 0) {
        observersRef.current.entrySeq().forEach(([_, observer]) => {
          observer.disconnect();
        });
      }
    },
    [],
  );

  return {
    parentElRef,
    hiddenCount,
    lastVisibleKey,
    lastKey,
    isAllHidden,
    setElementRef,
    elementsVisibilityState,
  };
};

export default useObserverTags;
